"use client";
import {
  ButtonProps,
  ButtonTypes,
  ComponentProps,
  ContentInteractionEvent,
  IconPositions,
} from "@/baseComponents";
import { MediaImage } from "@/components/atoms/media";
import { MediaImageData } from "@/components/atoms/media/MediaImage";
import { ButtonVariants, CardTypes, Cultures } from "@/types/enums";
import { buildImageUrl } from "@/utils/imageSharpUrlBuilder";
import {
  CardBody,
  CardFooter,
  chakra,
  Card as ChakraCard,
  Heading,
  LinkBox,
  LinkOverlay,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { decode } from "html-entities";
import DOMPurify from "isomorphic-dompurify";
import { MouseEvent, useRef, useState } from "react";
import { ContactIconProps, ContactLinks } from "../contactLinks";

export interface CardProps extends ComponentProps {
  id?: string;
  cardType: CardTypes;
  title: string;
  text?: string;
  tag?: string;
  date?: string;
  percentage?: string;
  contactLinks?: ContactIconProps[];
  mediaData?: MediaImageData;
  link?: ButtonProps;
  backgroundHexColor?: string;
  hideOverviewPublicationDate?: boolean;
  culture?: Cultures;
}

export default chakra(Card);
function Card({
  cardType,
  title,
  text,
  tag,
  contactLinks,
  percentage,
  mediaData,
  link,
  date,
  backgroundHexColor,
  hideOverviewPublicationDate = false,
  culture = Cultures["en-US"],
  theme = "brand",
  ...props
}: CardProps) {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const cardRef = useRef<HTMLElement | null>(null);

  // State to track the index of the hovered card
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0,
  });

  const styles = useMultiStyleConfig("CardConfig", {
    variant: cardType,
    colorScheme: theme,
  });

  const Title = (): JSX.Element => {
    const sanitizedTitle = DOMPurify.sanitize(title.replace("&nbsp;", ""), {
      ALLOWED_TAGS: [],
    });
    return link && link.url ? (
      <LinkOverlay
        href={link.url}
        target={link.target}
        download={link.isDownloadLink}
        onClick={() => {
          ContentInteractionEvent({
            interactionType: "click",
            blockName: "card",
            clickText: title,
            fileName: link.url,
          });
        }}
      >
        {decode(sanitizedTitle)}
      </LinkOverlay>
    ) : (
      <> {decode(sanitizedTitle)}</>
    );
  };

  function handleMouseMove(e: MouseEvent) {
    if (!cardRef.current || !imageRef.current || window.innerWidth < 960)
      return;

    const card = cardRef.current as HTMLElement;
    const image = imageRef.current;
    const xOffset = 60;
    const cardRect = card.getBoundingClientRect();

    setMousePosition({
      x: e.clientX - cardRect.left + xOffset,
      y: e.clientY - cardRect.top - image.clientHeight / 2,
    });
  }

  const newDate = date ? new Date(date) : undefined;
  const formattedDate = newDate?.toLocaleDateString(culture, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const cardText =
    cardType === CardTypes.Publication && !hideOverviewPublicationDate
      ? formattedDate
      : text;
  return (
    <motion.div
      style={{ height: "100%" }}
      onMouseMove={(e: React.MouseEvent) => handleMouseMove(e)}
      {...props}
    >
      <LinkBox
        ref={cardRef}
        as="article"
        height="full"
        role="group"
        position="relative"
      >
        <ChakraCard
          sx={{ ...styles.card, backgroundColor: backgroundHexColor }}
          height="full"
        >
          <CardBody p={0}>
            {tag && cardType !== CardTypes.Person && (
              <Text sx={styles.tag}>{tag}</Text>
            )}
            {title && (
              <Heading as="h2" sx={styles.heading}>
                <Title />
              </Heading>
            )}
            {cardText && <Text sx={styles.text}>{cardText}</Text>}
          </CardBody>
          <CardFooter p={0} justifyContent="end">
            {percentage && cardType === CardTypes.Facts && (
              <Text
                fontSize="4.5rem"
                color={`${theme}.white`}
                fontWeight={300}
                lineHeight="70%"
              >
                {percentage}
              </Text>
            )}
          </CardFooter>
        </ChakraCard>
        {mediaData && (
          <motion.div
            ref={imageRef}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              zIndex: 2,
              x: mousePosition.x,
              y: mousePosition.y,
            }}
          >
            <MediaImage
              {...mediaData}
              viewportWidthFactor={{ base: 0.2, md: 0.25 }}
              cropSizeAlias={{ base: "square" }}
              hasBorderRadius={true}
              imageUrlBuilder={buildImageUrl}
              width={"20rem"}
              height={"20rem"}
              opacity={0}
              pointerEvents="none"
              zIndex={2}
              display={{ base: "none", lg: "block" }}
              transition="opacity 0.5s ease-in-out"
              _groupHover={{
                opacity: { base: 0, lg: 1 },
              }}
            />
          </motion.div>
        )}
      </LinkBox>
    </motion.div>
  );
}
